import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/Layout/MainLayout.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { Textarea } from '../components/ui/textarea.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Lightbulb, Check, X, Trash2, Clock, Send, CalendarClock, User, Save, Play, PauseCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CATEGORIES = [
  { v: 'rh', l: 'RH' },
  { v: 'esg', l: 'ESG' },
  { v: 'remuneracao', l: 'Remuneração' },
  { v: 'beneficios', l: 'Benefícios' },
  { v: 'outro', l: 'Outro' },
];

const TYPES = [
  { v: 'percentual', l: 'Percentual (%)' },
  { v: 'numero', l: 'Número' },
  { v: 'monetario', l: 'Monetário (R$)' },
  { v: 'texto', l: 'Texto' },
];

const CRONOGRAMA_STATUS = [
  { v: 'nao_iniciado', l: 'Não iniciado', cls: 'bg-slate-100 text-slate-700', icon: PauseCircle },
  { v: 'em_andamento', l: 'Em andamento', cls: 'bg-blue-100 text-blue-700', icon: Play },
  { v: 'concluido',    l: 'Concluído',    cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  { v: 'atrasado',     l: 'Atrasado',     cls: 'bg-red-100 text-red-700', icon: AlertTriangle },
];

const StatusBadge = ({ status }) => {
  const map = {
    pending:  { l: 'Pendente',  cls: 'bg-amber-100 text-amber-700',     icon: Clock },
    approved: { l: 'Aprovado',  cls: 'bg-emerald-100 text-emerald-700', icon: Check },
    rejected: { l: 'Rejeitado', cls: 'bg-red-100 text-red-700',         icon: X },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.cls}`} data-testid={`status-${status}`}>
      <Icon className="w-3 h-3" />{m.l}
    </span>
  );
};

const CronogramaBadge = ({ status }) => {
  const m = CRONOGRAMA_STATUS.find(s => s.v === status) || CRONOGRAMA_STATUS[0];
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.cls}`} data-testid={`cronograma-${status}`}>
      <Icon className="w-3 h-3" />{m.l}
    </span>
  );
};

const ProgressBar = ({ value, status }) => {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const color =
    status === 'concluido' ? 'bg-emerald-500' :
    status === 'atrasado'  ? 'bg-red-500' :
    status === 'em_andamento' ? 'bg-blue-500' : 'bg-slate-300';
  return (
    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-testid="progress-bar">
      <div className={`h-full ${color} transition-all`} style={{ width: `${v}%` }} />
    </div>
  );
};

const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(isoDate + 'T00:00:00');
  return Math.round((d - today) / 86400000);
};

const CustomIndicators = () => {
  const userRole = localStorage.getItem('userRole') || 'respondent';
  const isAdmin = userRole === 'admin' || userRole === 'manager';
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCronograma, setFilterCronograma] = useState('all');
  const [showMine, setShowMine] = useState(false);
  const [form, setForm] = useState({
    nome: '', categoria: 'rh', tipo_dado: 'percentual', descricao: '', justificativa: '',
    andamento: 0, status_cronograma: 'nao_iniciado', prazo: '', responsavel: '',
  });
  const [feedback, setFeedback] = useState({});
  const [progressEdit, setProgressEdit] = useState({}); // {id: {andamento, status_cronograma, prazo, responsavel}}
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (showMine) params.set('mine', 'true');
      const url = `${API}/custom-indicators${params.toString() ? '?' + params.toString() : ''}`;
      const { data } = await axios.get(url, auth());
      setItems(data.items || []);
    } catch (e) { console.error(e); }
  }, [filterStatus, showMine]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { toast.error('Informe o nome do indicador'); return; }
    setLoading(true);
    try {
      const andamento = Number(form.andamento) || 0;
      const status_cronograma = andamento >= 100 ? 'concluido' : form.status_cronograma;
      await axios.post(`${API}/custom-indicators`, {
        ...form,
        andamento,
        status_cronograma,
        prazo: form.prazo || null,
      }, auth());
      toast.success('Indicador enviado para análise');
      setForm({
        nome: '', categoria: 'rh', tipo_dado: 'percentual', descricao: '', justificativa: '',
        andamento: 0, status_cronograma: 'nao_iniciado', prazo: '', responsavel: '',
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar');
    } finally { setLoading(false); }
  };

  const review = async (id, decision) => {
    try {
      await axios.post(`${API}/custom-indicators/${id}/review`, {
        decision,
        feedback: feedback[id] || '',
      }, auth());
      toast.success(decision === 'approved' ? 'Indicador aprovado' : 'Indicador rejeitado');
      setFeedback(p => ({ ...p, [id]: '' }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remover este indicador?')) return;
    try {
      await axios.delete(`${API}/custom-indicators/${id}`, auth());
      toast.success('Removido');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const saveProgress = async (id) => {
    const p = progressEdit[id];
    if (!p) return;
    const andamento = Number(p.andamento);
    // Auto-marcar concluído no client quando slider === 100
    // (garante que o badge/KPI seja atualizado corretamente após salvar)
    const status_cronograma = andamento >= 100 ? 'concluido' : p.status_cronograma;
    try {
      await axios.patch(`${API}/custom-indicators/${id}/progress`, {
        andamento,
        status_cronograma,
        prazo: p.prazo || null,
        responsavel: p.responsavel || '',
      }, auth());
      toast.success('Cronograma atualizado');
      setProgressEdit(prev => { const n = { ...prev }; delete n[id]; return n; });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao atualizar'); }
  };

  const startEditProgress = (it) => {
    setProgressEdit(prev => ({
      ...prev,
      [it.id]: {
        andamento: it.andamento ?? 0,
        status_cronograma: it.status_cronograma || 'nao_iniciado',
        prazo: it.prazo || '',
        responsavel: it.responsavel || '',
      },
    }));
  };

  const filtered = items.filter(it => {
    if (filterCronograma === 'all') return true;
    return (it.status_cronograma || 'nao_iniciado') === filterCronograma;
  });

  const stats = {
    pending: items.filter(i => i.status === 'pending').length,
    approved: items.filter(i => i.status === 'approved').length,
    rejected: items.filter(i => i.status === 'rejected').length,
    atrasado: items.filter(i => i.status_cronograma === 'atrasado').length,
    em_andamento: items.filter(i => i.status_cronograma === 'em_andamento').length,
    concluido: items.filter(i => i.status_cronograma === 'concluido').length,
  };

  const myId = (() => {
    try { return JSON.parse(atob(localStorage.getItem('token').split('.')[1])).sub; } catch { return null; }
  })();

  return (
    <MainLayout title="Indicadores Customizados" userRole={userRole}>
      <div className="space-y-6" data-testid="custom-indicators-page">
        {/* Header / KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-xl p-4 text-white">
            <p className="text-[11px] opacity-60 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Total Sugerido</p>
            <p className="text-2xl font-bold mt-1">{items.length}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4">
            <p className="text-[11px] text-amber-600">Pendentes</p>
            <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4">
            <p className="text-[11px] text-emerald-600">Aprovados</p>
            <p className="text-2xl font-bold text-emerald-700">{stats.approved}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4">
            <p className="text-[11px] text-red-600">Rejeitados</p>
            <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
          </div>
        </div>

        {/* KPIs de cronograma */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-blue-50 rounded-xl p-3">
            <p className="text-[11px] text-blue-600 flex items-center gap-1"><Play className="w-3 h-3" /> Em Andamento</p>
            <p className="text-xl font-bold text-blue-700">{stats.em_andamento}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3">
            <p className="text-[11px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Concluídos</p>
            <p className="text-xl font-bold text-emerald-700">{stats.concluido}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-3">
            <p className="text-[11px] text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Atrasados</p>
            <p className="text-xl font-bold text-red-700">{stats.atrasado}</p>
          </div>
        </div>

        {/* Sugerir novo */}
        <Card data-testid="suggest-form">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4 text-blue-600" /> Sugerir Novo Indicador</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Nome do Indicador *</Label>
                <Input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Taxa de Promoção Interna" data-testid="form-nome" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Categoria *</Label>
                  <Select value={form.categoria} onValueChange={v => setForm({ ...form, categoria: v })}>
                    <SelectTrigger data-testid="form-categoria"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tipo de Dado *</Label>
                  <Select value={form.tipo_dado} onValueChange={v => setForm({ ...form, tipo_dado: v })}>
                    <SelectTrigger data-testid="form-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Descrição</Label>
                <Textarea value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} rows={2} placeholder="Como o indicador é calculado / o que mede" data-testid="form-descricao" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Justificativa de Negócio</Label>
                <Textarea value={form.justificativa} onChange={e => setForm({ ...form, justificativa: e.target.value })} rows={2} placeholder="Por que este indicador deve ser adicionado ao benchmark" data-testid="form-justificativa" />
              </div>

              {/* Gestão de Cronograma */}
              <div className="md:col-span-2 border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1">
                  <CalendarClock className="w-3.5 h-3.5 text-blue-600" /> Gestão de Cronograma (opcional)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">% Andamento</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="range" min={0} max={100} step={5}
                        value={form.andamento}
                        onChange={e => setForm({ ...form, andamento: Number(e.target.value) })}
                        data-testid="form-andamento-slider"
                        className="flex-1"
                      />
                      <span className="text-sm font-semibold text-blue-700 w-12 text-right" data-testid="form-andamento-value">{form.andamento}%</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Status Cronograma</Label>
                    <Select value={form.status_cronograma} onValueChange={v => setForm({ ...form, status_cronograma: v })}>
                      <SelectTrigger data-testid="form-status-cronograma"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CRONOGRAMA_STATUS.filter(s => s.v !== 'atrasado').map(s => (
                          <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Prazo</Label>
                    <Input
                      type="date"
                      value={form.prazo}
                      onChange={e => setForm({ ...form, prazo: e.target.value })}
                      data-testid="form-prazo"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Responsável</Label>
                    <Input
                      value={form.responsavel}
                      onChange={e => setForm({ ...form, responsavel: e.target.value })}
                      placeholder="Ex: Time de RH"
                      data-testid="form-responsavel"
                    />
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={loading} data-testid="form-submit">
                  {loading ? 'Enviando...' : 'Enviar Sugestão'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Listagem */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Sugestões {showMine ? '(Minhas)' : (isAdmin ? '(Todas)' : '(Minhas)')}</CardTitle>
            <div className="flex gap-2 items-center flex-wrap">
              {isAdmin && (
                <Button variant={showMine ? 'default' : 'outline'} size="sm" onClick={() => setShowMine(!showMine)} data-testid="toggle-mine">
                  {showMine ? 'Ver Todas' : 'Apenas Minhas'}
                </Button>
              )}
              <Select value={filterCronograma} onValueChange={setFilterCronograma}>
                <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="filter-cronograma"><SelectValue placeholder="Cronograma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos cronogramas</SelectItem>
                  {CRONOGRAMA_STATUS.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos status</SelectItem>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="approved">Aprovados</SelectItem>
                  <SelectItem value="rejected">Rejeitados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8" data-testid="empty-state">Nenhuma sugestão encontrada.</p>
            ) : (
              <div className="space-y-3">
                {filtered.map(it => {
                  const isMine = it.suggested_by === myId;
                  const canDelete = (isAdmin || (isMine && it.status === 'pending'));
                  const canEditProgress = isAdmin || isMine;
                  const isEditing = !!progressEdit[it.id];
                  const dU = daysUntil(it.prazo);
                  const stc = it.status_cronograma || 'nao_iniciado';
                  return (
                    <div key={it.id} className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-testid={`indicator-${it.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{it.nome}</h3>
                            <StatusBadge status={it.status} />
                            <CronogramaBadge status={stc} />
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase tracking-wide">{it.categoria}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{TYPES.find(t => t.v === it.tipo_dado)?.l}</span>
                          </div>
                          {it.descricao && <p className="text-sm text-slate-600 mt-2">{it.descricao}</p>}
                          {it.justificativa && <p className="text-xs text-slate-500 mt-1 italic">Justificativa: {it.justificativa}</p>}

                          {/* Barra de progresso + meta */}
                          <div className="mt-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <ProgressBar value={it.andamento} status={stc} />
                              <span className="text-xs font-semibold text-slate-700 w-10 text-right" data-testid={`andamento-value-${it.id}`}>{it.andamento ?? 0}%</span>
                            </div>
                            <div className="flex items-center gap-4 text-[11px] text-slate-500 flex-wrap">
                              {it.prazo && (
                                <span className={`flex items-center gap-1 ${stc === 'atrasado' ? 'text-red-600 font-semibold' : ''}`} data-testid={`prazo-${it.id}`}>
                                  <CalendarClock className="w-3 h-3" />
                                  Prazo: {new Date(it.prazo + 'T00:00:00').toLocaleDateString('pt-BR')}
                                  {dU !== null && (
                                    <span className="ml-1 opacity-80">
                                      ({dU < 0 ? `${Math.abs(dU)}d em atraso` : dU === 0 ? 'hoje' : `faltam ${dU}d`})
                                    </span>
                                  )}
                                </span>
                              )}
                              {it.responsavel && (
                                <span className="flex items-center gap-1"><User className="w-3 h-3" /> {it.responsavel}</span>
                              )}
                              {it.progress_updated_at && (
                                <span className="opacity-70">Atualizado {new Date(it.progress_updated_at).toLocaleDateString('pt-BR')} {it.progress_updated_by && `por ${it.progress_updated_by}`}</span>
                              )}
                            </div>
                          </div>

                          <p className="text-[10px] text-slate-400 mt-2">
                            Sugerido por <strong>{it.suggested_by_company}</strong> em {new Date(it.suggested_at).toLocaleDateString('pt-BR')}
                            {it.reviewed_at && (
                              <> · Revisado em {new Date(it.reviewed_at).toLocaleDateString('pt-BR')}</>
                            )}
                          </p>
                          {it.feedback && (
                            <div className="mt-2 px-3 py-2 bg-slate-50 rounded text-xs text-slate-700 border-l-2 border-blue-400">
                              <strong>Feedback:</strong> {it.feedback}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          {canEditProgress && !isEditing && (
                            <button
                              onClick={() => startEditProgress(it)}
                              className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                              data-testid={`edit-progress-${it.id}`}
                              title="Atualizar cronograma"
                            >
                              <CalendarClock className="w-3.5 h-3.5" /> Atualizar
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-500" data-testid={`delete-${it.id}`} title="Remover">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Edição inline de cronograma */}
                      {isEditing && (
                        <div className="mt-3 pt-3 border-t border-slate-100 bg-slate-50/60 rounded-lg p-3" data-testid={`progress-editor-${it.id}`}>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                              <Label className="text-xs">% Andamento</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input
                                  type="range" min={0} max={100} step={5}
                                  value={progressEdit[it.id].andamento}
                                  onChange={e => setProgressEdit(p => ({ ...p, [it.id]: { ...p[it.id], andamento: Number(e.target.value) } }))}
                                  className="flex-1"
                                  data-testid={`edit-andamento-slider-${it.id}`}
                                />
                                <span className="text-sm font-semibold text-blue-700 w-12 text-right">{progressEdit[it.id].andamento}%</span>
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Status</Label>
                              <Select
                                value={progressEdit[it.id].status_cronograma}
                                onValueChange={v => setProgressEdit(p => ({ ...p, [it.id]: { ...p[it.id], status_cronograma: v } }))}
                              >
                                <SelectTrigger data-testid={`edit-status-cronograma-${it.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {CRONOGRAMA_STATUS.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Prazo</Label>
                              <Input
                                type="date"
                                value={progressEdit[it.id].prazo}
                                onChange={e => setProgressEdit(p => ({ ...p, [it.id]: { ...p[it.id], prazo: e.target.value } }))}
                                data-testid={`edit-prazo-${it.id}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Responsável</Label>
                              <Input
                                value={progressEdit[it.id].responsavel}
                                onChange={e => setProgressEdit(p => ({ ...p, [it.id]: { ...p[it.id], responsavel: e.target.value } }))}
                                data-testid={`edit-responsavel-${it.id}`}
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setProgressEdit(p => { const n = { ...p }; delete n[it.id]; return n; })}
                              data-testid={`cancel-progress-${it.id}`}
                            >Cancelar</Button>
                            <Button
                              size="sm"
                              onClick={() => saveProgress(it.id)}
                              data-testid={`save-progress-${it.id}`}
                            >
                              <Save className="w-3.5 h-3.5 mr-1" /> Salvar
                            </Button>
                          </div>
                        </div>
                      )}

                      {isAdmin && it.status === 'pending' && (
                        <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2 items-center flex-wrap">
                          <Input
                            placeholder="Feedback (opcional)"
                            value={feedback[it.id] || ''}
                            onChange={e => setFeedback(p => ({ ...p, [it.id]: e.target.value }))}
                            className="flex-1 min-w-[200px] h-8 text-xs"
                            data-testid={`feedback-input-${it.id}`}
                          />
                          <Button size="sm" onClick={() => review(it.id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid={`approve-${it.id}`}>
                            <Check className="w-3.5 h-3.5 mr-1" /> Aprovar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => review(it.id, 'rejected')} className="border-red-300 text-red-600 hover:bg-red-50 h-8" data-testid={`reject-${it.id}`}>
                            <X className="w-3.5 h-3.5 mr-1" /> Rejeitar
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default CustomIndicators;
